import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {RGBA} from '@Utils/color.util';
import {convertFabricColorToFillObject, rgbaArrayToRgbString, rgbaToHexString} from '@Utils/color.util';
import type {QRLogoReference, QRRenderConfig} from '@Libraries/qr-code/qr-code.types';
import {
  QR_BACKGROUND_CORNER_RADIUS_MAX,
  QR_DEFAULT_BACKGROUND_COLOR,
  QR_DEFAULT_BACKGROUND_CORNER_RADIUS,
  QR_DEFAULT_FOREGROUND_COLOR,
  QR_DEFAULT_LOGO_SIZE_RATIO,
  QR_DEFAULT_MARGIN,
  QRCodeDotType,
  QRCodeEyeBallType,
  QRCodeEyeFrameType,
  QRType,
} from '@Libraries/qr-code/qr-code.types';
import {QR_GENERATION_SIZE_PX, renderQRCodeBlob} from '@Libraries/qr-code/qr-render.library';
import {ElementDataType} from '@Libraries/add-media-library';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {FabricQRCodeImage} from '@PosterWhiteboard/items/fabric-qr-code-image';
import type {BaseItemObject, CopyableItemStyles, InitItemOpts, ItemObject} from './item/item.types';
import {ITEM_TYPE} from './item/item.types';
import type {FillObject} from '@PosterWhiteboard/classes/fill.class';
import {Fill, FillTypes} from '@PosterWhiteboard/classes/fill.class';
import type {DeepPartial} from '@/global';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import {BorderType} from '@PosterWhiteboard/classes/item-border.class';
import {loadImageAsync} from '@Utils/image.util';
import {blobToDataUrl} from '@Utils/file.util';
import type {ImageItemSourceType} from '@Libraries/image-item.library';
import {ImageItemSource} from '@Libraries/image-item.library';
import {getQRPlatformFromLogoReference, getQRPlatformLogoUrl} from '@Libraries/qr-code/qr-social-platform.library';
import {getQRLogoHighResUrls, getQRLogoThumbUrl, inlineFirstAvailableImage} from '@Libraries/qr-code/qr-logo.library';

export interface QRCodeItemObject extends BaseItemObject {
  message: string;
  qrType: QRType;
  foregroundFill: FillObject;
  backgroundFill: FillObject;
  margin: number;
  backgroundCornerRadius: number;
  dotStyle: QRCodeDotType;
  eyeFrameStyle: QRCodeEyeFrameType;
  eyeBallStyle: QRCodeEyeBallType;
  logoHashedFilename: string | null;
  logoFileExtension: string | null;
  logoSource: ImageItemSourceType;
  logoSizeRatio: number;
}

/**
 * Narrows a redux `ItemObject` to a `QRCodeItemObject`, so group hooks can read QR-only keys (e.g.
 * `foregroundFill`, `message`) without an unchecked cast.
 */
export const isQRCodeItemObject = (item: ItemObject): item is QRCodeItemObject => {
  return item.gitype === ITEM_TYPE.QR_CODE;
};

export const DEFAULT_QR_MESSAGE = 'https://www.postermywall.com';
export const DEFAULT_QR_MAX_LENGTH = 1400;

const createFill = (fillType: FillTypes, fillColor: RGBA[]): Fill => {
  const fill = new Fill();
  fill.fillType = fillType;
  fill.fillColor = fillColor;
  return fill;
};

export class QRCodeItem extends Item {
  public message = DEFAULT_QR_MESSAGE;
  public qrType: QRType = QRType.LINK;
  public foregroundFill: Fill = createFill(FillTypes.SOLID, [QR_DEFAULT_FOREGROUND_COLOR]);
  public backgroundFill: Fill = createFill(FillTypes.SOLID, [QR_DEFAULT_BACKGROUND_COLOR]);
  public margin = QR_DEFAULT_MARGIN;
  public backgroundCornerRadius = QR_DEFAULT_BACKGROUND_CORNER_RADIUS;
  public dotStyle: QRCodeDotType = QRCodeDotType.SQUARE;
  public eyeFrameStyle: QRCodeEyeFrameType = QRCodeEyeFrameType.SQUARE;
  public eyeBallStyle: QRCodeEyeBallType = QRCodeEyeBallType.SQUARE;
  public logoHashedFilename: string | null = null;
  public logoFileExtension: string | null = null;
  public logoSource: ImageItemSourceType = ImageItemSource.UPLOAD;
  public logoSizeRatio = QR_DEFAULT_LOGO_SIZE_RATIO;
  public gitype: ITEM_TYPE.QR_CODE = ITEM_TYPE.QR_CODE;

  public override copyVals(obj: DeepPartial<QRCodeItemObject>): void {
    const {foregroundFill, backgroundFill, ...plainObj} = obj;
    super.copyVals(plainObj);
    this.foregroundFill.copyVals(foregroundFill);
    this.backgroundFill.copyVals(backgroundFill);
  }

  public override async updateFromObject(obj: DeepPartial<QRCodeItemObject>, opts: UpdateFromObjectOpts = {}): Promise<void> {
    await super.updateFromObject(this.#withRoundnessResetWhenContainerRemoved(obj), opts);
  }

  /**
   * When an update turns off the last visible container edge (background and outline both
   * off), the hidden roundness resets in the same undoable operation, so the roundness
   * control starts from zero whenever it reappears. Updates that set the radius themselves
   * (full-snapshot loads, undo/redo diffs) pass through untouched.
   */
  #withRoundnessResetWhenContainerRemoved(obj: DeepPartial<QRCodeItemObject>): DeepPartial<QRCodeItemObject> {
    if (obj.backgroundCornerRadius !== undefined || this.backgroundCornerRadius === 0) {
      return obj;
    }

    const updatedFillType = obj.backgroundFill?.fillType ?? this.backgroundFill.fillType;
    const updatedBorderType = obj.border?.solidBorderType ?? this.border.solidBorderType;
    if (updatedFillType === FillTypes.NONE && updatedBorderType === BorderType.NONE) {
      return {...obj, backgroundCornerRadius: 0};
    }
    return obj;
  }

  protected override itemObjectHasDestructiveChanges(oldItemObject: QRCodeItemObject): boolean {
    return (
      oldItemObject.message !== this.message ||
      JSON.stringify(oldItemObject.foregroundFill) !== JSON.stringify(this.foregroundFill.toObject()) ||
      JSON.stringify(oldItemObject.backgroundFill) !== JSON.stringify(this.backgroundFill.toObject()) ||
      oldItemObject.margin !== this.margin ||
      oldItemObject.backgroundCornerRadius !== this.backgroundCornerRadius ||
      oldItemObject.dotStyle !== this.dotStyle ||
      oldItemObject.eyeFrameStyle !== this.eyeFrameStyle ||
      oldItemObject.eyeBallStyle !== this.eyeBallStyle ||
      oldItemObject.logoHashedFilename !== this.logoHashedFilename ||
      oldItemObject.logoFileExtension !== this.logoFileExtension ||
      oldItemObject.logoSource !== this.logoSource ||
      oldItemObject.logoSizeRatio !== this.logoSizeRatio
    );
  }

  public override getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
      frontFill: rgbaArrayToRgbString(this.foregroundFill.fillColor[0]),
      backFill: this.backgroundFill.toObject(),
    };
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<QRCodeItemObject> = {};
    if (copiedStyles.frontFill !== undefined) {
      stylesObj.foregroundFill = {
        fillType: FillTypes.SOLID,
        fillColor: [convertFabricColorToFillObject(copiedStyles.frontFill).fillColor[0]],
      };
    }

    if (copiedStyles.backFill !== undefined) {
      stylesObj.backgroundFill = copiedStyles.backFill;
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['shadow', 'border', 'flipX', 'flipY', 'scaleX', 'scaleY']);
    await this.applyPastedStyles({...commonStylesObj, ...stylesObj});
  }

  public override isPDFSafe(): Promise<boolean> {
    // The container border is drawn in FabricQRCodeImage._render only; rasterizing for
    // PDF/SVG generation captures it, a vector export would not.
    return Promise.resolve(false);
  }

  public override hasClickableLink(): boolean {
    return false;
  }

  public override toObject(): QRCodeItemObject {
    return {
      ...super.toObject(),
      message: this.message,
      qrType: this.qrType,
      foregroundFill: this.foregroundFill.toObject(),
      backgroundFill: this.backgroundFill.toObject(),
      margin: this.margin,
      backgroundCornerRadius: this.backgroundCornerRadius,
      dotStyle: this.dotStyle,
      eyeFrameStyle: this.eyeFrameStyle,
      eyeBallStyle: this.eyeBallStyle,
      logoHashedFilename: this.logoHashedFilename,
      logoFileExtension: this.logoFileExtension,
      logoSource: this.logoSource,
      logoSizeRatio: this.logoSizeRatio,
    };
  }

  public hasLogo(): boolean {
    return this.logoHashedFilename !== null && this.logoFileExtension !== null;
  }

  public hasTransparentBackground(): boolean {
    return !this.backgroundFill.hasFill();
  }

  public toggleBackgroundStateForQRItem = (): void => {
    void this.updateFromObject({
      backgroundFill: {
        fillType: this.backgroundFill.hasFill() ? FillTypes.NONE : FillTypes.SOLID,
      },
    });
  };

  protected async getFabricObjectForItem(opts: InitItemOpts = {}): Promise<FabricQRCodeImage> {
    const imageElement = await this.#generateQRCodeImage();
    return new FabricQRCodeImage(imageElement, {
      ...this.getCommonOptions(),
      scaleX: opts?.scaleX ?? 1,
      scaleY: opts?.scaleY ?? 1,
      ...this.getScalesAndShadowToMaintainScaledDimensions(imageElement.width, imageElement.height, opts),
      ...this.#getContainerBorderProps(),
    });
  }

  /**
   * The generic rect stroke cannot follow the container's corner radius, so the border is
   * drawn by FabricQRCodeImage instead of the base class's fabric stroke.
   */
  protected override applyBorder(): void {
    this.fabricObject.set(this.#getContainerBorderProps());
  }

  #getContainerBorderProps(): {qrBorderColor: string; qrBorderThickness: number; qrBorderRadius: number} {
    return {
      qrBorderColor: rgbaToHexString(this.border.solidBorderColor),
      qrBorderThickness: this.border.hasBorder() ? this.border.solidBorderThickness : 0,
      qrBorderRadius: (this.backgroundCornerRadius / QR_BACKGROUND_CORNER_RADIUS_MAX) * (QR_GENERATION_SIZE_PX / 2),
    };
  }

  protected override onItemDoubleTapped(): void {
    getPosterUi().openPosterEditorQRContentModal();
  }

  async #generateQRCodeImage(): Promise<HTMLImageElement> {
    const blob = await renderQRCodeBlob(await this.#getRenderConfig(), 'png');
    return loadImageAsync(await blobToDataUrl(blob));
  }

  async #getRenderConfig(): Promise<QRRenderConfig> {
    return {
      data: this.message,
      sizePx: QR_GENERATION_SIZE_PX,
      marginPercent: this.margin,
      backgroundCornerRadiusPercent: this.backgroundCornerRadius,
      dotStyle: this.dotStyle,
      eyeFrameStyle: this.eyeFrameStyle,
      eyeBallStyle: this.eyeBallStyle,
      foregroundFill: this.foregroundFill.toObject(),
      backgroundFill: this.backgroundFill.toObject(),
      logoDataUrl: await this.#getLogoDataUrl(),
      logoSizeRatio: this.logoSizeRatio,
    };
  }

  /**
   * A logo that cannot be inlined must not take the whole QR code down with it: the item still has a
   * message to render, and an aborted generation leaves the canvas untouched, so the click that applied
   * the logo looks like it did nothing at all. Degrade to a logo-less QR code and report why.
   */
  async #getLogoDataUrl(): Promise<string | null> {
    if (!this.hasLogo()) {
      return null;
    }
    return inlineFirstAvailableImage(this.#getLogoUrlCandidates());
  }

  /**
   * A mark carried over from the free QR tool is one of ours, served by the app rather than held in any
   * media store, so it resolves by platform and needs no CORS grant — same-origin is the whole point.
   *
   * A logo the user supplied resolves through its own source. The editor draws it from the thumb; a
   * high-res render tries the screen tier and then the legacy screen file, for uploads that never got tiers.
   */
  #getLogoUrlCandidates(): string[] {
    const hashedFilename = this.logoHashedFilename ?? '';
    const platform = getQRPlatformFromLogoReference(hashedFilename);
    if (platform !== null) {
      const platformLogoUrl = getQRPlatformLogoUrl(platform);
      return platformLogoUrl === null ? [] : [platformLogoUrl];
    }
    const logo: QRLogoReference = {hashedFilename, fileExtension: this.logoFileExtension ?? '', source: this.logoSource};
    return this.page.poster.isHighRes ? getQRLogoHighResUrls(logo) : [getQRLogoThumbUrl(logo)];
  }
}

export const addQrCodeToPage = (message?: string, top?: number, left?: number): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }

  const opts = {message: message ?? window.posterEditor?.config?.businessProfileUrl ?? DEFAULT_QR_MESSAGE};
  void currentPage.items.addItems.addQRItem(
    {
      type: ElementDataType.QR_CODE,
      ...(top !== undefined && {top}),
      ...(left !== undefined && {left}),
    },
    opts
  );
};
